import dayjs from 'dayjs';
import { FormikValues } from 'formik';

import {
  Resource,
  FormBlock,
  FormField,
  FormFieldType,
  FormSubmissionData,
  OutcomesFieldValue,
  OutcomeFieldsFormField,
  OutcomeFieldsFieldValue,
} from 'types';

import { getPlural } from './helpers';
import { formatDuration } from './date';
import { getShouldShowFieldInputIds } from './storage';
import {
  buildOrderedOutcomes,
  normalizeOutcomesFieldValue,
  generateFormFieldsValidationSchema,
} from './validation';

export const getOutcomeFieldsSourceValue = (
  field: OutcomeFieldsFormField,
  submissionData: FormSubmissionData | null
): OutcomesFieldValue => {
  const value = normalizeOutcomesFieldValue(
    submissionData?.[field.options?.sourceFieldId ?? '']
  );
  const outcomes = buildOrderedOutcomes(value.outcomes, value.orderedIds);
  const maxOutcomeCount = field.options?.maxOutcomeCount;
  const limitedOutcomes =
    typeof maxOutcomeCount === 'number'
      ? outcomes.slice(0, maxOutcomeCount)
      : outcomes;

  return {
    outcomes: limitedOutcomes,
    orderedIds: limitedOutcomes.map((item) => item.id),
  };
};

export const appendInputIdToLabel = (
  label?: string,
  inputId?: string | number
) => {
  const baseLabel = label ?? '';

  if (
    !baseLabel ||
    inputId === undefined ||
    inputId === null ||
    inputId === ''
  ) {
    return baseLabel;
  }

  return getShouldShowFieldInputIds()
    ? `${baseLabel} [${String(inputId)}]`
    : baseLabel;
};

export const collectOutcomesFields = (
  fields: FormField[]
): Extract<FormField, { type: FormFieldType.OUTCOMES }>[] =>
  fields.flatMap((field) => {
    if (field.type === FormFieldType.OUTCOMES) {
      return [field];
    }

    if (field.type === FormFieldType.FIELD_ARRAY && field.options?.fields) {
      return collectOutcomesFields(field.options.fields);
    }

    return [];
  });

export const collectOutcomeFields = (
  fields: FormField[]
): Extract<FormField, { type: FormFieldType.OUTCOME_FIELDS }>[] =>
  fields.filter(
    (
      field
    ): field is Extract<FormField, { type: FormFieldType.OUTCOME_FIELDS }> =>
      field.type === FormFieldType.OUTCOME_FIELDS
  );

export const normalizeOutcomeFieldsValue = (
  value: unknown
): OutcomeFieldsFieldValue => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(value).filter(
      ([, item]) =>
        typeof item === 'object' && item !== null && !Array.isArray(item)
    )
  );
};

const getMergedOutcomeFieldsOutcomes = (
  field: OutcomeFieldsFormField,
  submissionData: FormSubmissionData | null
) => {
  const sourceValue = getOutcomeFieldsSourceValue(field, submissionData);
  const storedValue = normalizeOutcomeFieldsValue(submissionData?.[field.id]);
  const storedOutcomes = Object.entries(storedValue).map(([id, item]) => ({
    id,
    label: typeof item.label === 'string' ? item.label : id,
    ...(typeof item.description === 'string'
      ? { description: item.description }
      : {}),
  }));
  const sourceOrderedIdSet = new Set(sourceValue.orderedIds);
  const orderedIds = sourceValue.orderedIds.length
    ? [
        ...sourceValue.orderedIds,
        ...storedOutcomes
          .map(({ id }) => id)
          .filter((id) => !sourceOrderedIdSet.has(id)),
      ]
    : storedOutcomes.map(({ id }) => id);
  const outcomesById = new Map(
    storedOutcomes.map((outcome) => [outcome.id, outcome])
  );

  sourceValue.outcomes.forEach((outcome) => {
    outcomesById.set(outcome.id, outcome);
  });

  return {
    storedValue,
    outcomes: buildOrderedOutcomes(
      Array.from(outcomesById.values()),
      orderedIds
    ),
  };
};

export const getInitialValuesForFields = (
  fields: FormBlock['fields'],
  submissionData: FormSubmissionData | null
) => {
  let initialValues: FormikValues = {};

  fields.forEach((field) => {
    if (field.type === FormFieldType.SCALE_RATING) {
      initialValues[field.id] =
        submissionData?.[field.id] ??
        field.options?.options?.map(() => null) ??
        [];
    } else if (field.type === FormFieldType.OUTCOMES) {
      initialValues[field.id] = normalizeOutcomesFieldValue(
        submissionData?.[field.id]
      );
    } else if (field.type === FormFieldType.OUTCOME_FIELDS) {
      const { outcomes, storedValue } = getMergedOutcomeFieldsOutcomes(
        field,
        submissionData
      );

      initialValues[field.id] = Object.fromEntries(
        outcomes.map((outcome) => {
          const nestedInitialValues = getInitialValuesForFields(
            field.options?.fields ?? [],
            (storedValue[outcome.id] as FormSubmissionData | undefined) ?? null
          );

          return [
            outcome.id,
            {
              ...nestedInitialValues,
              label: outcome.label,
              ...(outcome.description
                ? { description: outcome.description }
                : {}),
            },
          ];
        })
      );
    } else if (field.type === FormFieldType.FIELD_ARRAY) {
      if (field.options?.fields) {
        initialValues = {
          ...initialValues,
          ...getInitialValuesForFields(field.options?.fields, submissionData),
        };
      }
    } else if (
      field.type === FormFieldType.SELECT &&
      !!field.options?.multiple
    ) {
      initialValues[field.id] = submissionData?.[field.id] ?? [];
    } else if (
      field.type === FormFieldType.INPUT &&
      field.options?.type === 'date'
    ) {
      const value = submissionData?.[field.id];
      initialValues[field.id] = value ? dayjs(value as string) : null;
    } else {
      initialValues[field.id] = submissionData?.[field.id] ?? '';
    }
  });
  return initialValues;
};

export const getInitialValuesForForm = (
  fields: FormField[],
  submissionData: FormSubmissionData | null
) => getInitialValuesForFields(fields, submissionData);

export const getDataForFormikForm = (
  fields: FormField[],
  submissionData: FormSubmissionData | null
) => {
  return {
    initialValues: getInitialValuesForForm(fields, submissionData),
    validationSchema: generateFormFieldsValidationSchema(
      fields,
      submissionData
    ),
  };
};

export const getResourceDuration = (resource: Resource): string => {
  const duration = resource.data.blocks.reduce(
    (total, block) => total + block.duration,
    0
  );
  return `${formatDuration(duration)} ${getPlural('min', Math.floor(duration / 60), { onlyWord: true })}`;
};

export const fixSubmissionTypes = (
  payload: FormSubmissionData,
  fields: FormField[]
): FormSubmissionData => {
  let newPayload = { ...payload };
  fields.forEach(({ id, type, options }) => {
    const isFieldArray = type === FormFieldType.FIELD_ARRAY;
    if (
      newPayload[id] !== undefined &&
      newPayload[id] !== null &&
      !isFieldArray
    ) {
      if (type === FormFieldType.INPUT && options?.type === 'number') {
        newPayload[id] = Number(newPayload[id]);
      } else if (type === FormFieldType.OUTCOMES) {
        newPayload[id] = normalizeOutcomesFieldValue(newPayload[id]);
      } else if (type === FormFieldType.OUTCOME_FIELDS) {
        const value = normalizeOutcomeFieldsValue(newPayload[id]);
        newPayload[id] = Object.fromEntries(
          Object.entries(value).map(([outcomeId, outcomeValue]) => [
            outcomeId,
            fixSubmissionTypes(outcomeValue, options?.fields ?? []),
          ])
        );
      }
    } else if (isFieldArray && options?.fields) {
      newPayload = fixSubmissionTypes(newPayload, options.fields);
    }
  });
  return newPayload;
};

export const splitFieldsByPages = (fields: FormField[]) =>
  fields.reduce(
    (pages, field) => {
      if (field.type === FormFieldType.PAGE_BREAK) {
        // Start a new page
        pages.push([]);
      } else {
        // Add field to current page
        if (pages.length === 0) pages.push([]); // Ensure at least one page exists
        pages[pages.length - 1].push(field);
      }
      return pages;
    },
    [] as Array<Array<FormField>>
  );

export const getFieldIndexWidthClass = (questionsCount: number) =>
  questionsCount >= 10 ? 'w-6' : 'w-3';
